import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  NotificationEventCatalogueResponse,
  NotificationTrigger,
  NotificationTriggerCreateInput,
  NotificationTriggerListResponse,
  NotificationTriggerLogListResponse,
  NotificationTriggerUpdateInput,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase F0b — NotificationTrigger CRUD hooks for the IA Settings →
 * Notifications page.
 *
 * The BE (PR #85) ships `/notifications/triggers` + `/event-catalogue`
 * + `/triggers/{id}/logs`. These hooks are deliberately thin wrappers —
 * the editor sheet handles its own validation and surfaces 409 (name
 * collision) / 422 (empty channels-recipients) errors inline.
 *
 * Cache keys:
 *   - `["notification-triggers"]`           → list (active by default)
 *   - `["notification-triggers","with-archived"]` → list including
 *      soft-deleted rows for the archive view
 *   - `["notification-triggers", id]`       → single row read for the
 *      "View dispatch history" sheet (avoids re-fetching when the
 *      sheet opens after a fresh PATCH)
 *   - `["notification-triggers", id, "logs"]` → per-trigger dispatch
 *      history (manual refetch only — auto-refetch would hammer the
 *      collection on long-open sheets)
 *   - `["notification-event-catalogue"]`    → read-only event picker
 *      data (long stale window)
 */

export const NOTIFICATION_TRIGGERS_QUERY_KEY = ["notification-triggers"] as const;
export const NOTIFICATION_EVENT_CATALOGUE_QUERY_KEY = ["notification-event-catalogue"] as const;

export function useNotificationEventCatalogue() {
  return useQuery<NotificationEventCatalogueResponse>({
    queryKey: NOTIFICATION_EVENT_CATALOGUE_QUERY_KEY,
    queryFn: () => api.get<NotificationEventCatalogueResponse>("/notifications/event-catalogue"),
    // Catalogue is append-only and changes per phase release. A 5-minute
    // stale window is generous enough to avoid hammering the BE while
    // still picking up new events on the next open after a deploy.
    staleTime: 5 * 60 * 1000,
  });
}

export function useNotificationTriggers(options?: { includeArchived?: boolean }) {
  const includeArchived = options?.includeArchived ?? false;
  return useQuery<NotificationTriggerListResponse>({
    queryKey: includeArchived
      ? [...NOTIFICATION_TRIGGERS_QUERY_KEY, "with-archived"]
      : NOTIFICATION_TRIGGERS_QUERY_KEY,
    queryFn: () => {
      const qs = includeArchived ? "?include_archived=true" : "";
      return api.get<NotificationTriggerListResponse>(`/notifications/triggers${qs}`);
    },
    staleTime: 30 * 1000,
  });
}

export function useNotificationTrigger(triggerId: string | null | undefined) {
  return useQuery<NotificationTrigger>({
    queryKey: [...NOTIFICATION_TRIGGERS_QUERY_KEY, triggerId ?? "__missing__"],
    queryFn: () => api.get<NotificationTrigger>(`/notifications/triggers/${triggerId}`),
    enabled: Boolean(triggerId),
  });
}

export function useNotificationTriggerLogs(triggerId: string | null | undefined) {
  return useQuery<NotificationTriggerLogListResponse>({
    queryKey: [...NOTIFICATION_TRIGGERS_QUERY_KEY, triggerId ?? "__missing__", "logs"],
    queryFn: () =>
      api.get<NotificationTriggerLogListResponse>(
        `/notifications/triggers/${triggerId}/logs?limit=50`,
      ),
    enabled: Boolean(triggerId),
    // Logs are append-only and the sheet is short-lived; 30s is enough
    // to coalesce duplicate opens but stale enough that a user who
    // dispatches a test fire and then reopens sees the new row.
    staleTime: 30 * 1000,
  });
}

function invalidateLists(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: NOTIFICATION_TRIGGERS_QUERY_KEY });
}

export function useCreateNotificationTrigger() {
  const qc = useQueryClient();
  return useMutation<NotificationTrigger, Error, NotificationTriggerCreateInput>({
    mutationKey: ["notification-triggers", "create"],
    mutationFn: (body) => api.post<NotificationTrigger>("/notifications/triggers", body),
    onSuccess: () => invalidateLists(qc),
  });
}

export interface UseUpdateNotificationTriggerVars {
  id: string;
  body: NotificationTriggerUpdateInput;
}

export function useUpdateNotificationTrigger() {
  const qc = useQueryClient();
  return useMutation<NotificationTrigger, Error, UseUpdateNotificationTriggerVars>({
    mutationKey: ["notification-triggers", "update"],
    mutationFn: ({ id, body }) =>
      api.patch<NotificationTrigger>(`/notifications/triggers/${id}`, body),
    onSuccess: () => invalidateLists(qc),
  });
}

/** Flips `enabled`. Separate from PATCH so the audit log surfaces a
 * dedicated `notification_trigger.toggled.on/off` action — much cleaner
 * for the compliance-export view than a generic `updated`. */
export function useToggleNotificationTrigger() {
  const qc = useQueryClient();
  return useMutation<NotificationTrigger, Error, string>({
    mutationKey: ["notification-triggers", "toggle"],
    mutationFn: (id) => api.post<NotificationTrigger>(`/notifications/triggers/${id}:toggle`, {}),
    onSuccess: () => invalidateLists(qc),
  });
}

/** Soft-delete (sets `deleted_at`). The dispatcher will treat the
 * trigger as inactive on its next event-fire, but the per-trigger
 * `NotificationLog` rows stay greppable for forensics. */
export function useArchiveNotificationTrigger() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, Error, string>({
    mutationKey: ["notification-triggers", "archive"],
    mutationFn: (id) => api.delete<{ ok: boolean }>(`/notifications/triggers/${id}`),
    onSuccess: () => invalidateLists(qc),
  });
}
